import { useState, useCallback, useRef, useEffect } from 'react';
import { speechService } from '@/services/speech';

export function useVoiceInput() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const removeListenerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    speechService.isAvailable().then(setIsSupported);
  }, []);

  const startListening = useCallback(async () => {
    if (isListening) return;
    setTranscript('');
    setIsListening(true);

    try {
      removeListenerRef.current = await speechService.addListener('partialResults', (data) => {
        if (data.matches && data.matches[0]) {
          setTranscript(data.matches[0]);
        }
      });

      await speechService.start({ language: 'en-US', partialResults: true });
    } catch {
      setIsListening(false);
    }
  }, [isListening]);

  const stopListening = useCallback(async () => {
    if (!isListening) return;
    try {
      await speechService.stop();
    } catch {
      // ignore
    }
    removeListenerRef.current?.();
    removeListenerRef.current = null;
    setIsListening(false);
  }, [isListening]);

  const clearTranscript = useCallback(() => {
    setTranscript('');
  }, []);

  // Auto-stop if Capacitor speech ends (web SpeechRecognition handles this via onend)
  useEffect(() => {
    if (!isListening) return;

    const timeout = setTimeout(() => {
      // Safety timeout - if listening for too long, auto-stop
      stopListening();
    }, 30000);

    return () => clearTimeout(timeout);
  }, [isListening, stopListening]);

  return {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
    clearTranscript,
  };
}
